import { MESSAGES } from "constants/Messages";
import { Action, Thunk, action, thunk } from "easy-peasy";
import Commission from "pages/applications/tabs/underwriting/Commission";
import { post, get, del, put } from "requests";

export interface HeldBackComissionModel {
  heldbacks: any[];
  commissions: any[];
  brokersList: any[];
  transactionTypes: any[];
  commissionTypes: any[];
  externalFunders: any[];
  bankFeeAlert: boolean;
  syndicatorBankFeeAlert: boolean;
  bankFeeMessage: string;
  investorBankFeeRequest: any;
  commissionAmount: any;
  totalCommission: any;

  isLoading: boolean;
  error: string | null;

  setHeldbacks: Action<HeldBackComissionModel, any[]>;
  setCommissions: Action<HeldBackComissionModel, any[]>;
  setExternalFunders: Action<HeldBackComissionModel, any[]>;

  setLoading: Action<HeldBackComissionModel, boolean>;
  setBrokersList: Action<HeldBackComissionModel, any[]>;
  setError: Action<HeldBackComissionModel, string | null>;
  setTransactionTypes: Action<HeldBackComissionModel, any[]>;
  setCommissionTypes: Action<HeldBackComissionModel, any[]>;
  setBankFeeAlert: Action<HeldBackComissionModel, boolean>;
  setSyndicatorBankFeeAlert: Action<HeldBackComissionModel, any>;
  setBankFeeMessage: Action<HeldBackComissionModel, string>;
  setInvestorBankFeeRequest: Action<HeldBackComissionModel, any>;
  setTotalCommission: Action<HeldBackComissionModel, any>;
  setCommissionAmount: Action<HeldBackComissionModel, any>;

  fetchCommissions: Thunk<HeldBackComissionModel, any>;

  handleForTotalBankFeeOrInvestorEditChanges: Thunk<HeldBackComissionModel, any>;
  loadHeldbackList: Thunk<
    HeldBackComissionModel,
    {
      callback?: Function | undefined;
      applicationId: any;
      updateFee?: boolean;
      offerDetails: any | undefined;
    }
  >;
  deleteCommission: Thunk<
    HeldBackComissionModel,
    {
      id: string;
      callback?: Function;
      bankFeeSwitch?: boolean;
      type: string;
      applicationId: any;
      deletingIds: any[];
    }
  >;
  saveBankFeeHeldBack: Thunk<
    HeldBackComissionModel,
    {
      heldback: any;
      offerDetails: any;
      callback?: Function;
      dealApplicationId: any;
      noReload?: boolean;
    }
  >;
  saveHeldback: Thunk<
    HeldBackComissionModel,
    { offerDetails: any; dealApplicationId: any; heldbackData: any; callback?: Function }
  >;
  validateTotalBankFeeAmount: Thunk<
    HeldBackComissionModel,
    { dealApplicationId: any; heldback: any; offerDetails: any; callback: Function }
  >;
  saveCommission: Thunk<
    HeldBackComissionModel,
    { commissionData: any; offerDetails: any; updateSyndicatorsPercentage: Function }
  >;

  onInit: Thunk<HeldBackComissionModel, any>;
  refresh: Thunk<HeldBackComissionModel, any>;
  handlePostComissionUpdate: Thunk<
    HeldBackComissionModel,
    { dealApplicationId: any; offerDetails: any; callback?: Function }
  >;
  handleBankFeeSwitchChanged: Thunk<HeldBackComissionModel, any>;
  updateManualBankFee: Thunk<HeldBackComissionModel, any>;
  updateDefaultSyndicatorBankFee: Thunk<HeldBackComissionModel, any>;
  updateDefaultManualSyndicatorBankFee: Thunk<HeldBackComissionModel, any>;
  getBrokersList: Thunk<HeldBackComissionModel>;
  getTransactionTypes: Thunk<HeldBackComissionModel>;
  getExternalFunders: Thunk<HeldBackComissionModel, any>;

  loadCommissionTypes: Thunk<HeldBackComissionModel>;
  updateOrDeleteBankFeeForBroker: Thunk<HeldBackComissionModel, { broker: any; offerDetails: any }>;
  updateBankFeeOfBrokerAsInvestor: Thunk<
    HeldBackComissionModel,
    { bankFeeUpdate: any; bankFeeHeldBack: any; heldBackBroker: any; offerDetails: any }
  >;
  resetStore: Action<HeldBackComissionModel>;
}

const HeldBackComissionStore: HeldBackComissionModel = {
  heldbacks: [],
  commissions: [],
  brokersList: [],
  transactionTypes: [],
  commissionTypes: [],
  externalFunders: [],
  isLoading: false,
  error: null,
  bankFeeAlert: false,
  bankFeeMessage: "",
  syndicatorBankFeeAlert: null,
  investorBankFeeRequest: null,
  commissionAmount: null,
  totalCommission: null,

  setHeldbacks: action((state, payload) => {
    state.heldbacks = payload;
  }),

  setInvestorBankFeeRequest: action((state, payload) => {
    state.investorBankFeeRequest = payload;
  }),

  setBankFeeAlert: action((state, payload) => {
    state.bankFeeAlert = payload;
  }),

  setExternalFunders: action((state, payload) => {
    state.externalFunders = payload;
  }),

  setSyndicatorBankFeeAlert: action((state, payload) => {
    state.syndicatorBankFeeAlert = payload;
  }),

  setBankFeeMessage: action((state, payload) => {
    state.bankFeeMessage = payload;
  }),

  setTotalCommission: action((state, payload) => {
    state.totalCommission = payload;
  }),

  setCommissionAmount: action((state, payload) => {
    state.commissionAmount = payload;
  }),
  setCommissionTypes: action((state, payload) => {
    state.commissionTypes = payload;
  }),

  setTransactionTypes: action((state, payload) => {
    state.transactionTypes = payload;
  }),

  setBrokersList: action((state, payload) => {
    state.brokersList = payload;
  }),

  setCommissions: action((state, payload) => {
    state.commissions = payload;
  }),

  setLoading: action((state, payload) => {
    state.isLoading = payload;
  }),

  setError: action((state, payload) => {
    state.error = payload;
  }),

  resetStore: action((state) => {
    state.commissions = [];
    state.heldbacks = [];
    state.error = null;
  }),

  fetchCommissions: thunk(async (actions, dealApplicationId) => {
    try {
      // actions.setLoading(true);
      const response = await post(`/fetchApplicationCommissions`, {
        applicationId: dealApplicationId,
        type: "inhouse_commission",
      });
      const commisions = response?.commissions ? response?.commissions : [];
      actions.setCommissionAmount(response?.commissionAmount || 0);
      actions.setTotalCommission(response?.commissionTotal || 0);
      actions.setCommissions(commisions);
      return response;
    } catch (error) {
      actions.setError("Failed to fetch commissions");
    } finally {
      // actions.setLoading(false);
    }
  }),

  saveCommission: thunk(
    async (
      actions,
      { commissionData, offerDetails, updateSyndicatorsPercentage },
      { getState }
    ) => {
      const state = getState();
      let oldCommission: any = null;

      let broker: any = null;
      if (commissionData?.applicationPartyId) {
        broker = state.brokersList.find(
          (broker) => broker?.id === commissionData?.applicationPartyId
        );
      }

      const request: any = {
        name: broker?.name,
        partyId: commissionData?.applicationPartyId,
        description: commissionData?.description,
        amount: commissionData?.amount,
        isInvestor: commissionData?.isInvestor,
        type: "inhouse_commission",
        commissionPercentageAmount: commissionData?.percentageOfCommission,
        commissionTypeName: commissionData?.commissionTypeId,
        commissionTypeId: commissionData?.commissionTypeId?.id,
        tenantId: "1",
        applicationId: commissionData?.applicationId,
      };

      if (commissionData?.id) {
        request["id"] = commissionData?.id;
      }

      if (commissionData?.dayIndex) {
        request["dayIndex"] = Number(commissionData?.dayIndex);
      }

      if (commissionData?.id) {
        oldCommission = state.commissions.find((comm) => comm.id === commissionData?.id);
      }

      if (oldCommission) {
        request["oldId"] = oldCommission.id;
        request["oldPartyId"] = oldCommission.partyId;
        request["oldInvested"] = oldCommission.isInvestor;
      }

      if (commissionData?.newBrokerParty) {
        request.newBrokerParty = commissionData?.newBrokerParty;
      }
      // actions.setLoading(true);

      try {
        await actions.updateOrDeleteBankFeeForBroker({
          broker: request,
          offerDetails: offerDetails,
        });
        if (request.isInvestor && oldCommission && request.partyId !== oldCommission?.partyId) {
          await actions.deleteCommission({
            id: request.oldId,
            bankFeeSwitch: undefined,
            type: request.type,
            applicationId: request?.applicationId,
            callback: undefined,
            deletingIds: undefined,
          });

          delete request["id"];
          delete request["oldId"];
          delete request["oldPartyId"];
          delete request["oldInvested"];
          await post("createOrUpdateApplicationCommission", request);
          await updateSyndicatorsPercentage({ applicationId: request?.applicationId });
        } else {
          await post("createOrUpdateApplicationCommission", request);
          await updateSyndicatorsPercentage({ applicationId: request?.applicationId });
        }
      } catch (error) {
        actions.setError("Failed to save commission");
      } finally {
        // actions.setLoading(false);
      }
    }
  ),

  loadHeldbackList: thunk(async (actions, { applicationId, callback, updateFee, offerDetails }) => {
    try {
      // actions.setLoading(true);
      const response = await post("fetchApplicationHeldbacks", { applicationId: applicationId });
      const responseData = response["heldbacks"];

      if (responseData) {
        actions.setHeldbacks(responseData);
        if (updateFee) {
          offerDetails["bankFee"] = response["bankFee"];
          offerDetails["totalHeldback"] = response["totalHeldback"];
          offerDetails["netFundingAmount"] = response["netFundingAmount"];
        }

        if (callback) {
          await callback(responseData);
        }
      }
    } catch (error) {
      actions.setError("Failed to load heldback list");
    } finally {
      // actions.setLoading(false);
    }
  }),

  deleteCommission: thunk(
    async (
      actions,
      { id, callback, bankFeeSwitch, type, applicationId, deletingIds },
      { getState }
    ) => {
      const requestData = {
        id: id,
        type: type,
        applicationId: applicationId,
        deletingIds: deletingIds,
      };

      try {
        const responseData = await post("applicationCommissionAndSyndications/delete", requestData);
        await actions.loadHeldbackList({
          callback: undefined,
          applicationId: applicationId,
          offerDetails: undefined,
          updateFee: true,
        });

        if (responseData) {
          if (callback) {
            await callback();
          }
        }
      } catch (error) {
        console.error("Error deleting commission:", error);
        actions.setError("Failed to delete commission");
      }
    }
  ),

  getExternalFunders: thunk(async (actions) => {
    const tenantId = localStorage.getItem("tenantId");
    const response = await get(`fetchExternalFunders/${tenantId}`);
    actions.setExternalFunders(response || []);
  }),

  handleForTotalBankFeeOrInvestorEditChanges: thunk(
    async (
      actions,
      { offerDetails, dealApplicationId, callback, skipPartyIds = [] },
      { getState }
    ) => {
      const tenantId = localStorage.getItem("tenantId");
      const funder = offerDetails.contractTerms ? offerDetails.contractTerms["funder"] : null;
      const defaultSyndicatorPartyId =
        funder && funder.defaultSyndicatorId ? funder["defaultSyndicatorId"] : funder["id"];
      const totalBankFee = offerDetails.bankFee;
      const copyOfOfferDetails = JSON.parse(JSON.stringify(offerDetails));
      let defaultSyndicatorHeldBack: any = null;
      let remainingBankFee = 0;

      // actions.setLoading(true);

      try {
        const dealApplication = await get(`/getDealApplication/${dealApplicationId}`);
        dealApplication.bankFee = totalBankFee;
        dealApplication.bankFeePercentage = copyOfOfferDetails?.bankFeePercentage;

        if (typeof dealApplication.userRoles === "string") {
          try {
            dealApplication.userRoles = JSON.parse(dealApplication.userRoles);
          } catch (error) {
            console.error("Invalid JSON in userRoles:", error);
            dealApplication.userRoles = [];
          }
        }

        const { userRolesList, ...dealApplicationModel } = dealApplication;
        await put(`/dealApplications/updateBankFee`, dealApplicationModel);
        const syndicatorsResponse = await get(
          `fetchDealApplicationSyndicators/${dealApplicationId}`
        );
        const investors = syndicatorsResponse["syndicatorList"];

        if (investors && investors.length > 0) {
          await actions.loadHeldbackList({
            callback: async () => {
              const state = getState();
              const { heldbacks } = state;
              const heldbackDistribution = heldbacks.filter(
                (heldback) => heldback.type === "bank_fee"
              );

              if (
                heldbackDistribution.length > 0 &&
                (totalBankFee === "" || parseFloat(totalBankFee) === 0)
              ) {
                const deletingIds = heldbackDistribution.map((heldback) => heldback.id);
                await actions.deleteCommission({
                  id: deletingIds[0],
                  type: "",
                  applicationId: dealApplicationId,
                  deletingIds: [],
                  callback: undefined,
                });
              } else if (
                heldbackDistribution.length === 0 &&
                totalBankFee !== "" &&
                parseFloat(totalBankFee) > 0
              ) {
                const heldback = {
                  applicationId: dealApplicationId,
                  tenantId,
                  amount: totalBankFee,
                  type: "bank_fee",
                  paymentDate: new Date(),
                  totalBankFee,
                  partyId: defaultSyndicatorPartyId,
                };
                await actions.saveBankFeeHeldBack({
                  heldback: heldback,
                  offerDetails: undefined,
                  dealApplicationId: dealApplicationId,
                  callback: undefined,
                  noReload: true,
                });
              } else if (heldbackDistribution.length === 1) {
                let defaultBankFeeHeldBack = null;
                try {
                  defaultBankFeeHeldBack = heldbackDistribution[0];
                  defaultBankFeeHeldBack["amount"] = totalBankFee;
                  await actions.saveBankFeeHeldBack({
                    heldback: defaultBankFeeHeldBack,
                    offerDetails: undefined,
                    callback: undefined,
                    dealApplicationId: dealApplicationId,
                    noReload: false,
                  });
                } catch (e) {
                  console.log("error =>", e);
                }
              } else {
                for (let outerIndex = 0; outerIndex < heldbackDistribution.length; outerIndex++) {
                  const heldback = heldbackDistribution[outerIndex];
                  for (let index = 0; index < investors.length; index++) {
                    const investor = investors[index];
                    const investorBankFee = (
                      totalBankFee *
                      (parseFloat(investor.investmentPercentage) / 100)
                    ).toFixed(2);
                    if (!investor.defaultSyndicator && investor.isBankFee) {
                      if (
                        heldback.applicationPartyId === investor.applicationPartyId &&
                        !skipPartyIds.includes(investor.partyId)
                      ) {
                        heldback.amount = parseFloat(investorBankFee);
                        heldback.totalBankFee = totalBankFee;
                        remainingBankFee += parseFloat(investorBankFee);
                        await actions.saveBankFeeHeldBack({
                          heldback: heldback,
                          offerDetails: undefined,
                          callback: undefined,
                          dealApplicationId: dealApplicationId,
                          noReload: false,
                        });

                        break;
                      }
                    }
                  }

                  if (heldback.defaultSyndicator) {
                    defaultSyndicatorHeldBack = heldback;
                  }
                }

                if (remainingBankFee > 0 && defaultSyndicatorHeldBack) {
                  defaultSyndicatorHeldBack.amount = totalBankFee - remainingBankFee;
                  setTimeout(async () => {
                    await actions.saveBankFeeHeldBack({
                      heldback: defaultSyndicatorHeldBack,
                      offerDetails: undefined,
                      callback: undefined,
                      dealApplicationId: dealApplicationId,
                      noReload: false,
                    });
                  }, 500);
                }
              }
              if (callback) callback();
            },
            applicationId: dealApplicationId,
            updateFee: true,
            offerDetails: offerDetails,
          });
        }
      } catch (error) {
        actions.setError("Error handling bank fee or investor changes");
      } finally {
        // actions.setLoading(false);
      }
    }
  ),

  saveBankFeeHeldBack: thunk(
    async (actions, { heldback, offerDetails, dealApplicationId, callback, noReload }) => {
      try {
        const tenantId = localStorage.getItem("tenantId");
        heldback["tenantId"] = tenantId;
        heldback["partyId"] = heldback.partyId || heldback.applicationPartyId;
        heldback["name"] = heldback.name || heldback.applicationPartyName;
        heldback["applicationId"] = heldback.applicationId || dealApplicationId;

        console.log("SAVING ===>", heldback);

        const savedData = await post("createOrUpdateApplicationCommission", heldback);

        if (noReload) {
          if (callback) await callback();
        } else {
          actions.loadHeldbackList({
            callback: callback(),
            applicationId: dealApplicationId,
            offerDetails: undefined,
            updateFee: true,
          });
        }
      } catch (error) {
        actions.setError("Failed to save bank fee heldback");
      }
    }
  ),

  getBrokersList: thunk(async (actions) => {
    try {
      const response = await get("fetchBrokerParties/1");
      actions.setBrokersList(response["brokers"]);
    } catch (error) {
      actions.setError("Failed to get brokers");
    }
  }),

  getTransactionTypes: thunk(async (actions) => {
    try {
      const response = await get("dealTransaction/getHeldbackTransactionTypes/1");
      actions.setTransactionTypes(response);
    } catch (error) {
      actions.setError("Failed to get heldback types");
    }
  }),

  loadCommissionTypes: thunk(async (actions) => {
    const relationshipType = "broker";

    try {
      const responseData = await get(`feeAndCommission/fetchTypes/${relationshipType}`);
      console.log("=====feeAndCommission/fetchTypes = ", responseData);

      if (responseData.length > 0) {
        let commissionTypes = responseData.map((syndicatorFeesType: any) => ({
          label: syndicatorFeesType.name.replace("_", " ").toUpperCase(),
          value: { id: syndicatorFeesType.id, name: syndicatorFeesType.name },
          key: syndicatorFeesType,
        }));

        commissionTypes = commissionTypes.filter((comm: any) => comm.label !== "PER TRANSACTION");

        actions.setCommissionTypes(commissionTypes);
      }
    } catch (error) {
      console.error("Error loading commission types:", error);
    }
  }),

  validateTotalBankFeeAmount: thunk(
    async (actions, { dealApplicationId, heldback, offerDetails, callback }, { getState }) => {
      const state = getState();
      const contractTerms = offerDetails.contractTerms;
      const funder = contractTerms ? contractTerms["funder"] : null;
      const defaultSyndicatorId =
        funder && funder.defaultSyndicatorId ? funder["defaultSyndicatorId"] : funder["id"];
      const totalBankFee = offerDetails.bankFee;
      let remainingBankFeeAmount = 0;
      const type = heldback["type"].value;

      if (totalBankFee <= 0 || (type && type !== "bank_fee")) {
        await callback(true);
        return;
      }

      if (defaultSyndicatorId) {
        try {
          await actions.loadHeldbackList({
            applicationId: dealApplicationId,
            updateFee: undefined,
            offerDetails: offerDetails,
            callback: async (heldBacks: any) => {
              for (let index = 0; index < heldBacks.length; index++) {
                const heldback = heldBacks[index];
                if (heldback.type && heldback.type === "bank_fee") {
                  if (
                    heldback.applicationPartyId !== defaultSyndicatorId &&
                    heldback.applicationPartyId !== heldback.partyId
                  ) {
                    remainingBankFeeAmount += heldback.amount;
                  }

                  if (heldback.applicationPartyId === heldback.partyId) {
                    remainingBankFeeAmount += parseInt(heldback.amount);
                  }
                }
              }

              if (totalBankFee > 0 && remainingBankFeeAmount > 0) {
                if (remainingBankFeeAmount > totalBankFee) {
                  await callback(false);
                } else {
                  await callback(true);
                }
              } else {
                await callback(true);
              }
            },
          });
        } catch (error) {
          actions.setError("Error while validating bank fee amount.");
          await callback(false);
        }
      } else {
        await callback(true);
      }
    }
  ),

  saveHeldback: thunk(
    async (actions, { callback, offerDetails, heldbackData, dealApplicationId }) => {
      // actions.setLoading(true)
      try {
        await actions.validateTotalBankFeeAmount({
          dealApplicationId: dealApplicationId,
          heldback: heldbackData,
          offerDetails: offerDetails,
          callback: async (isValid: boolean) => {
            if (isValid) {
              heldbackData["tenantId"] = localStorage.getItem("tenantId");
              heldbackData["applicationId"] = dealApplicationId;

              let clonedHeldBack = { ...heldbackData };

              if (
                clonedHeldBack?.type !== undefined &&
                clonedHeldBack?.type === "bank_fee" &&
                heldbackData.originalType !== "bank_fee"
              ) {
                let id = heldbackData["id"];
                heldbackData["id"] = null;

                let deletingIds = [id];
                await actions.deleteCommission({
                  id: deletingIds[0],
                  type: "",
                  applicationId: dealApplicationId,
                  deletingIds: [],
                  callback: undefined,
                });

                await post("createOrUpdateApplicationCommission", heldbackData);
              } else if (
                heldbackData.isInvested &&
                heldbackData.partyId !== heldbackData.oldPartyId
              ) {
                await actions.deleteCommission({
                  id: heldbackData.oldId,
                  type: "",
                  applicationId: dealApplicationId,
                  deletingIds: [],
                  callback: undefined,
                });

                if (typeof heldbackData.type === "object" && heldbackData.type !== null) {
                  heldbackData.type = heldbackData.type.value;
                }

                await post("createOrUpdateApplicationCommission", heldbackData);
              } else {
                if (typeof heldbackData.type === "object" && heldbackData.type !== null) {
                  heldbackData.type = heldbackData.type.value;
                }

                await post("createOrUpdateApplicationCommission", heldbackData);
              }
              if (callback) {
                await callback(dealApplicationId);
              }
            } else {
              console.error("Validation failed for bank fee amount.");
            }
          },
        });
      } finally {
        // actions.setLoading(false)
      }
    }
  ),

  handleBankFeeSwitchChanged: thunk(
    async (actions, { offerDetails, investor, dealApplicationId }, { getState }) => {
      const state = getState();
      const contractTerms = offerDetails.contractTerms;
      const funder = contractTerms ? contractTerms["funder"] : null;
      const tenantId = localStorage.getItem("tenantId");
      const applicationId = dealApplicationId || investor.applicationId;
      const defaultSyndicatorPartyId = funder?.defaultSyndicatorId ?? funder?.id;

      const heldback: any = {
        applicationId: applicationId,
        tenantId: tenantId,
        type: "bank_fee",
        paymentDate: new Date(),
      };

      try {
        const dealApplication = await get(`/getDealApplication/${dealApplicationId}`);
        const totalBankFee = dealApplication.bankFee;

        investor.defaultSyndicatorPartyId = defaultSyndicatorPartyId;

        if (investor.isBankFee) {
          heldback.partyId = investor.applicationPartyId;
          heldback.name = investor.applicationPartyName;
          heldback.amount = (
            totalBankFee *
            (parseFloat(investor.investmentPercentage) / 100)
          ).toFixed(2);

          await actions.loadHeldbackList({
            applicationId,
            callback: async (heldbackDistribution: any) => {
              const defaultSyndicatorAmount = heldbackDistribution
                .filter((item: any) => item.type === "bank_fee" && item.defaultSyndicator)
                .reduce((sum: any, item: any) => sum + parseFloat(item.amount), 0);

              if (parseFloat(heldback.amount) <= defaultSyndicatorAmount) {
                await actions.saveBankFeeHeldBack({
                  heldback,
                  offerDetails,
                  dealApplicationId: applicationId,
                  callback: async () => {
                    await actions.updateDefaultSyndicatorBankFee({
                      investor,
                      totalBankFee,
                      offerDetails,
                      callback: undefined,
                    });
                  },
                  noReload: true,
                });
              } else {
                actions.setSyndicatorBankFeeAlert(MESSAGES.BANK_FEE_DISTRIBUTION_ALERT);
                // actions.setLoading(false);
              }
            },
            offerDetails,
          });
        } else {
          investor.applicationId = applicationId;
          investor.tenantId = tenantId;
          await actions.updateDefaultSyndicatorBankFee({
            investor,
            totalBankFee,
            offerDetails,
            callback: undefined,
          });
        }
      } catch (error) {
        console.error("Error in handleBankFeeSwitchChanged:", error);
        actions.setError("Failed to update bank fee");
        // actions.setLoading(false);
      }
    }
  ),

  updateManualBankFee: thunk(
    async (actions, { offerDetails, heldback, investor, dealApplicationId }, { getState }) => {
      const state = getState();
      const contractTerms = offerDetails.contractTerms;
      const funder = contractTerms ? contractTerms["funder"] : null;
      const tenantId = localStorage.getItem("tenantId");
      const applicationId = dealApplicationId || investor.applicationId;
      const defaultSyndicatorPartyId = funder?.defaultSyndicatorId ?? funder?.id;

      const validAmount = !isNaN(parseFloat(heldback.amount)) ? parseFloat(heldback.amount) : 0;
      const validOldAmount = !isNaN(parseFloat(heldback.oldAmount))
        ? parseFloat(heldback.oldAmount)
        : 0;
      const bankFeeChanged = validAmount - validOldAmount;

      try {
        const dealApplication = await get(`/getDealApplication/${dealApplicationId}`);
        const totalBankFee = dealApplication.bankFee;

        investor.defaultSyndicatorPartyId = defaultSyndicatorPartyId;

        if (investor.isBankFee) {
          heldback.partyId = investor.applicationPartyId;
          heldback.name = investor.applicationPartyName;

          await actions.loadHeldbackList({
            applicationId,
            callback: async (heldbackDistribution: any) => {
              const defaultSyndicatorAmount = heldbackDistribution
                .filter((item: any) => item.type === "bank_fee" && item.defaultSyndicator)
                .reduce((sum: any, item: any) => sum + parseFloat(item.amount), 0);
              if (parseFloat(heldback.amount) <= defaultSyndicatorAmount) {
                await actions.saveBankFeeHeldBack({
                  heldback,
                  offerDetails,
                  dealApplicationId: applicationId,
                  callback: async () => {
                    await actions.updateDefaultManualSyndicatorBankFee({
                      investor,
                      bankFee: bankFeeChanged,
                      offerDetails,
                      callback: undefined,
                    });
                  },
                  noReload: true,
                });
              } else {
                actions.setSyndicatorBankFeeAlert(MESSAGES.BANK_FEE_DISTRIBUTION_ALERT);
                // actions.setLoading(false);
              }
            },
            offerDetails,
          });
        } else {
          investor.applicationId = applicationId;
          investor.tenantId = tenantId;
          await actions.updateDefaultManualSyndicatorBankFee({
            investor,
            bankFee: bankFeeChanged,
            offerDetails,
            callback: undefined,
          });
        }
      } catch (error) {
        console.error("Error in handleBankFeeSwitchChanged:", error);
        actions.setError("Failed to update bank fee");
        // actions.setLoading(false);
      }
    }
  ),

  updateDefaultManualSyndicatorBankFee: thunk(
    async (actions, { investor, bankFee, offerDetails, callback }, { getState }) => {
      const state = getState();
      const defaultSyndicatorPartyId = investor?.defaultSyndicatorPartyId;
      let investorBankFeeRecordId: any = null;

      if (defaultSyndicatorPartyId) {
        await actions.loadHeldbackList({
          applicationId: investor.applicationId,
          callback: async (heldbackList: any) => {
            const bankFeeHeldbacks = heldbackList.filter((item: any) => item.type === "bank_fee");

            for (const currentHeldback of bankFeeHeldbacks) {
              if (currentHeldback.applicationPartyId === defaultSyndicatorPartyId) {
                const investorBankFee: any = bankFee;
                currentHeldback.amount = (
                  parseFloat(currentHeldback.amount) - parseFloat(investorBankFee)
                ).toFixed(2);

                if (currentHeldback?.amount > 0 && investor.isBankFee) {
                  await actions.saveBankFeeHeldBack({
                    heldback: currentHeldback,
                    offerDetails: offerDetails,
                    dealApplicationId: investor.applicationId,
                    callback,
                    noReload: true,
                  });
                }
              }

              if (currentHeldback.applicationPartyId === investor.partyId) {
                investorBankFeeRecordId = currentHeldback.id;
              }
            }

            if (investorBankFeeRecordId && !investor.isBankFee) {
              await actions.deleteCommission({
                id: investorBankFeeRecordId,
                type: "bank_fee",
                applicationId: investor.applicationId,
                callback,
                deletingIds: [],
              });
            } else if (callback) {
              await callback();
            }
          },
          offerDetails: offerDetails,
        });
      } else if (callback) {
        await callback();
      }
    }
  ),

  updateDefaultSyndicatorBankFee: thunk(
    async (actions, { investor, totalBankFee, offerDetails, callback }, { getState }) => {
      const state = getState();
      const defaultSyndicatorPartyId = investor?.defaultSyndicatorPartyId;
      let investorBankFeeRecordId: any = null;

      if (defaultSyndicatorPartyId) {
        await actions.loadHeldbackList({
          applicationId: investor.applicationId,
          callback: async (heldbackList: any) => {
            const bankFeeHeldbacks = heldbackList.filter((item: any) => item.type === "bank_fee");

            for (const currentHeldback of bankFeeHeldbacks) {
              if (currentHeldback.applicationPartyId === defaultSyndicatorPartyId) {
                const investorBankFee: any = totalBankFee
                  ? (totalBankFee * (parseFloat(investor.investmentPercentage) / 100)).toFixed(2)
                  : 0;

                if (investorBankFee > 0 && investor.isBankFee) {
                  currentHeldback.amount = (
                    parseFloat(currentHeldback.amount) - parseFloat(investorBankFee)
                  ).toFixed(2);
                  await actions.saveBankFeeHeldBack({
                    heldback: currentHeldback,
                    offerDetails: offerDetails,
                    dealApplicationId: investor.applicationId,
                    callback,
                    noReload: true,
                  });
                }
              }

              if (currentHeldback.applicationPartyId === investor.partyId) {
                investorBankFeeRecordId = currentHeldback.id;
              }
            }

            if (investorBankFeeRecordId && !investor.isBankFee) {
              await actions.deleteCommission({
                id: investorBankFeeRecordId,
                type: "bank_fee",
                applicationId: investor.applicationId,
                callback,
                deletingIds: [],
              });
            } else if (callback) {
              await callback();
            }
          },
          offerDetails: offerDetails,
        });
      } else if (callback) {
        await callback();
      }
    }
  ),

  onInit: thunk(async (actions, dealApplicationId) => {
    actions.resetStore();
    await actions.loadHeldbackList({
      applicationId: dealApplicationId,
      offerDetails: undefined,
      callback: undefined,
      updateFee: false,
    });
    await actions.fetchCommissions(dealApplicationId);
    await actions.getBrokersList();
    await actions.getTransactionTypes();
    await actions.loadCommissionTypes();
  }),

  refresh: thunk(async (actions, dealApplicationId) => {
    await actions.loadHeldbackList({
      applicationId: dealApplicationId,
      offerDetails: undefined,
      callback: undefined,
      updateFee: false,
    });
    await actions.fetchCommissions(dealApplicationId);
    await actions.getBrokersList();
  }),

  handlePostComissionUpdate: thunk(
    async (actions, { dealApplicationId, offerDetails, callback }) => {
      await actions.loadHeldbackList({
        applicationId: dealApplicationId,
        offerDetails: undefined,
        callback: undefined,
        updateFee: false,
      });
      const response = await actions.fetchCommissions(dealApplicationId);
      //     offerDetails.contractTerms.commissionAmount = response?.commissionTotal;
      //     console.log(response)
      //     console.log(offerDetails)
      //     const commissionAmount = parseFloat(response?.commissionTotal);
      //     const fundedAmount = parseFloat(offerDetails?.fundedAmount);
      //     let commissionPercentage = 0;
      //    if (!isNaN(commissionAmount) && !isNaN(fundedAmount) && fundedAmount !== 0) {
      //        commissionPercentage = (commissionAmount / fundedAmount) * 100;
      //      }
      //     offerDetails.contractTerms.commissionPercentage = commissionPercentage;
      //     offerDetails.applicationId=dealApplicationId;

      if (callback) {
        await callback(offerDetails);
      }

      await actions.getBrokersList();
    }
  ),

  updateOrDeleteBankFeeForBroker: thunk(async (actions, { broker, offerDetails }, { getState }) => {
    try {
      const state = getState();
      const heldbackList = state.heldbacks.filter((item) => item.type === "bank_fee");

      for (const heldback of heldbackList) {
        if (heldback.applicationPartyId === broker.partyId && !heldback.defaultSyndicator) {
          if (!broker.isInvestor && broker["oldInvested"] === true) {
            await actions.deleteCommission({
              id: heldback.id,
              bankFeeSwitch: null,
              type: null,
              applicationId: null,
              deletingIds: null,
              callback: () => {},
            });
          } else {
            if (broker.isInvestor && broker.oldAmount !== broker.amount) {
              if (offerDetails) {
                let fundedAmount = offerDetails.fundedAmount;
                let totalBankFee = offerDetails.bankFee;
                let percentage = (broker.oldAmount * 100) / fundedAmount;
                let heldbackOldBankFee = totalBankFee * (percentage / 100);
                heldbackOldBankFee = parseFloat((heldbackOldBankFee * 100).toFixed(2)) / 100;

                if (heldbackOldBankFee !== heldback.amount) {
                  actions.setInvestorBankFeeRequest({
                    bankFeeUpdate: true,
                    bankFeeHeldBack: heldback,
                    heldBackBroker: broker,
                    offerDetails: offerDetails,
                  });
                  actions.setBankFeeAlert(true);
                  actions.setBankFeeMessage(MESSAGES.BANK_FEE_ALERT_MESSAGE);
                } else {
                  actions.updateBankFeeOfBrokerAsInvestor({
                    bankFeeUpdate: true,
                    bankFeeHeldBack: heldback,
                    heldBackBroker: broker,
                    offerDetails: offerDetails,
                  });
                }
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("Error in updateOrDeleteBankFeeForBroker:", error);
      actions.setError("An error occurred while updating or deleting the bank fee.");
    }
  }),

  updateBankFeeOfBrokerAsInvestor: thunk(
    async (actions, { bankFeeUpdate, bankFeeHeldBack, heldBackBroker, offerDetails }) => {
      if (bankFeeUpdate) {
        if (offerDetails) {
          const fundedAmount = offerDetails.fundedAmount;
          const totalBankFee = offerDetails.bankFee;
          const percentage = (heldBackBroker.amount * 100) / fundedAmount;
          const heldbackBankFee = totalBankFee * (percentage / 100);

          const request: any = {
            id: null,
            type: { value: "bank_fee" },
            partyId: bankFeeHeldBack.applicationPartyId,
            name: bankFeeHeldBack.applicationPartyName,
            amount: heldbackBankFee,
            applicationId: bankFeeHeldBack.applicationId
              ? bankFeeHeldBack.applicationId
              : heldBackBroker.applicationId,
          };

          await actions.saveHeldback({
            heldbackData: request,
            dealApplicationId: heldBackBroker.applicationId,
            offerDetails: offerDetails,
            callback: undefined,
          });
          await actions.loadHeldbackList({
            applicationId: bankFeeHeldBack.applicationId
              ? bankFeeHeldBack.applicationId
              : heldBackBroker.applicationId,
            offerDetails: undefined,
            callback: undefined,
            updateFee: false,
          });
        }
      }
    }
  ),
};

export default HeldBackComissionStore;
